import { useTranslation } from 'next-i18next'
import { useEffect, useState } from 'react'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Divider from '~/bootcamp/Activities/components/Divider'
import FractionsActivityWrapper from '~/bootcamp/Activities/Fractions/components/FractionsActivityWrapper'
import { SelectionButton } from '~/bootcamp/Activities/Fractions/components/SelectionButton'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import { getRangesByDifficulty } from './constants'
import { generateTerms } from './helpers'

function FractionCancelEval({
  answerState,
  difficultyLevel,
  onNextQuestionClick,
  onAnswerStateChange,
  onInstanceChange,
  actionButtonsFC,
}: ActivityProps): React.ReactElement {
  const { t } = useTranslation('bootcamp')

  const [answer, setAnswer] = useState<boolean | null>(null)
  const [commonFactors, setCommonFactors] = useState<number[]>([])
  const [yesSelected, setYesSelected] = useState<boolean | null>(null)

  const [numerator, setNumerator] = useState<number>(1)
  const [denominator, setDenominator] = useState<number>(2)
  const [taskHistory, setTaskHistory] = useState<number[]>([])

  const { maxVariation } = getRangesByDifficulty(difficultyLevel)

  // `commonFactors`, `yesSelected`, and `answer` stay out: they carry whether this fraction reduces.
  // No rendered input, so no \square slot. The options are the painted yes/no buttons, in order.
  useEmitInstance(onInstanceChange, denominator ? `\\frac{${numerator}}{${denominator}}; options: yes, no` : null)

  useEffect(() => {
    setAnswer(null)
    setYesSelected(null)

    const {
      numerator: num,
      denominator: denom,
      commonFactors: comnFactors,
    } = generateTerms(difficultyLevel, taskHistory)

    setNumerator(num)
    setDenominator(denom)
    setCommonFactors(comnFactors)
  }, [difficultyLevel, taskHistory])

  useEffect(() => {
    onAnswerStateChange(null)
  }, [answer, onAnswerStateChange])

  const handleOnCheckAnswerClick = (): void => {
    const ansResult =
      (commonFactors.length === 0 && yesSelected === true) || (commonFactors.length !== 0 && yesSelected === false)
    onAnswerStateChange(ansResult)
  }

  const handleOnNextQuestionClick = (): void => {
    setTaskHistory((prevState) => [numerator, ...prevState].slice(0, maxVariation))
    onAnswerStateChange(null)
    onNextQuestionClick()
  }

  const handleOnOptionClick = (boolValue: boolean): void => {
    setYesSelected(boolValue)
    setAnswer(boolValue)
  }

  return (
    <ActivityWrapper>
      <FractionsActivityWrapper>
        <div className="w-6 flex flex-col items-center">
          {numerator}
          <Divider />
          {denominator}
        </div>
        <div className="w-full flex space-x-8 pt-10">
          <SelectionButton
            isSelected={yesSelected === true}
            isWrong={yesSelected === true && answerState === false}
            onClick={() => {
              handleOnOptionClick(true)
            }}
            size="medium"
          >
            {t('fractions.yes')}
          </SelectionButton>
          <SelectionButton
            isSelected={yesSelected === false}
            isWrong={yesSelected === false && answerState === false}
            onClick={() => {
              handleOnOptionClick(false)
            }}
            size="medium"
          >
            {t('fractions.no')}
          </SelectionButton>
        </div>
      </FractionsActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid: answer !== null,
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleOnCheckAnswerClick,
          clearFormAction: () => {
            setYesSelected(null)
            setAnswer(null)
          },
          goToNextQuestionAction: handleOnNextQuestionClick,
        })}
    </ActivityWrapper>
  )
}

export default FractionCancelEval
